import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { getProductByRowId, getProductOrders } from '../services/adminProductService';
import { getStore } from '../services/adminStoreService';
import { getProductReviews, hideReview, showReview } from '../services/adminReviewsService';
import StoreDetailModal from '../components/StoreDetailModal';
import TimeRangeSelector from '../components/TimeRangeSelector';
import { filterByTimeRange } from '../utils/timeRange';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { can } from '../utils/adminPermissions';
import { specRowsForProduct } from '../utils/productSpecs';
import { PRODUCTS_TABLE_ID, ORDERS_TABLE_ID } from '../lib/appwrite';
import './ProductDetailPage.css';

const formatDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  try {
    return dt.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dt.toISOString().replace('T', ' ').slice(0, 16);
  }
};

const formatCurrency = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  return n.toLocaleString('en-TZ', { style: 'decimal' });
};

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [store, setStore] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [detailModal, setDetailModal] = useState(null);
  const [analyticsTimeRange, setAnalyticsTimeRange] = useState('month');
  const [reviews, setReviews] = useState([]);
  const [reviewsSummary, setReviewsSummary] = useState({ count: 0, average: 0 });
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsLoaded, setReviewsLoaded] = useState(false);
  const [reviewsError, setReviewsError] = useState(null);
  const [reviewActing, setReviewActing] = useState({});
  // Hidden reviews drop out of the list endpoint entirely, so a "Show" undo
  // only works for what was hidden this session — tracked locally.
  const [locallyHidden, setLocallyHidden] = useState({});
  const { adminRole } = useOutletContext() || {};

  const loadData = useCallback(async () => {
    if (!productId) return;
    try {
      setLoading(true);
      setError(null);
      const prod = await getProductByRowId(productId);
      if (!prod) {
        setError('Product not found');
        setLoading(false);
        return;
      }
      setProduct(prod);
      const ordersRes = await getProductOrders(prod.productId, prod.productName);
      setOrders(ordersRes || []);

      if (prod.storeId) {
        try {
          const s = await getStore(prod.storeId);
          setStore(s);
        } catch {
          setStore(null);
        }
      } else {
        setStore(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to load product');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useAutoRefresh(loadData, { realtimeTables: [PRODUCTS_TABLE_ID, ORDERS_TABLE_ID] });

  const loadReviews = useCallback(async () => {
    if (!product?.productId) return;
    setReviewsLoading(true); setReviewsError(null);
    try {
      const res = await getProductReviews(product.productId);
      setReviews(res.reviews || []);
      setReviewsSummary(res.summary || { count: 0, average: 0 });
    } catch (err) {
      setReviewsError(err.message || 'Failed to load reviews');
    } finally {
      setReviewsLoading(false);
      setReviewsLoaded(true);
    }
  }, [product]);

  useEffect(() => {
    if (activeTab === 'reviews' && !reviewsLoaded && product?.productId) {
      loadReviews();
    }
  }, [activeTab, reviewsLoaded, product, loadReviews]);

  const canModerateReviews = can(adminRole, 'moderate', 'reviews');

  const toggleReviewVisibility = async (review, hide) => {
    setReviewActing((a) => ({ ...a, [review.id]: true }));
    try {
      if (hide) await hideReview(review.id);
      else await showReview(review.id);
      setLocallyHidden((h) => ({ ...h, [review.id]: hide }));
    } catch (err) {
      setReviewsError(err.message || 'Failed to update review');
    } finally {
      setReviewActing((a) => ({ ...a, [review.id]: false }));
    }
  };

  const specRows = specRowsForProduct(product);

  const unitsSold = orders.reduce((sum, o) => sum + (parseInt(o.quantity, 10) || 0), 0);
  const revenue = orders.reduce((sum, o) => sum + (parseFloat(o.totalAmount) || 0), 0);

  if (loading) {
    return (
      <div className="product-detail-loading">
        <div className="dashboard-spinner" />
        <p>Loading product...</p>
      </div>
    );
  }

  if (error && !product) {
    return (
      <div className="product-detail-page">
        <div className="product-detail-error">{error}</div>
        <button
          type="button"
          className="product-detail-back"
          onClick={() => navigate('/dashboard/products')}
        >
          ← Back to Products
        </button>
      </div>
    );
  }

  if (!product) return null;

  const TABS = [
    { id: 'overview', label: 'Overview', icon: '/icons/dashboard-square-01.svg' },
    { id: 'orders', label: 'Orders', icon: '/icons/shopping-cart-02.svg' },
    { id: 'analytics', label: 'Analytics', icon: '/icons/trending-up.svg' },
    { id: 'reviews', label: 'Reviews', icon: '/icons/eye.svg' },
  ];

  return (
    <div className="product-detail-page">
      <button
        type="button"
        className="product-detail-back"
        onClick={() => navigate('/dashboard/products')}
      >
        <img src="/icons/circle-arrow-left-02.svg" alt="" aria-hidden />
        Back to Products
      </button>

      {error && (
        <div className="product-detail-alert product-detail-alert-error">
          {error}
          <button type="button" onClick={() => setError(null)}>×</button>
        </div>
      )}

      <header className="product-detail-header">
        <div className="product-detail-header-main">
          <h1 className="product-detail-title">{product.productName || 'Unnamed Product'}</h1>
          <p className="product-detail-id">Product ID: {product.productId || product.$id}</p>
          <div className="product-detail-actions">
            <button
              type="button"
              className="product-detail-btn product-detail-btn-secondary"
              onClick={() => loadData()}
            >
              ⟳ Refresh
            </button>
          </div>
        </div>
        <div className="product-detail-stats">
          <div className="product-detail-stat-card">
            <span className="product-detail-stat-value">{orders.length}</span>
            <span className="product-detail-stat-label">Orders</span>
          </div>
          <div className="product-detail-stat-card">
            <span className="product-detail-stat-value">{unitsSold}</span>
            <span className="product-detail-stat-label">Units Sold</span>
          </div>
          <div className="product-detail-stat-card product-detail-stat-revenue">
            <span className="product-detail-stat-value">{formatCurrency(revenue)}</span>
            <span className="product-detail-stat-label">Revenue (TZS)</span>
          </div>
        </div>
      </header>

      <nav className="product-detail-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`product-detail-tab ${activeTab === tab.id ? 'product-detail-tab-active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="product-detail-tab-icon">
              <img src={tab.icon} alt="" aria-hidden />
            </span>
            {tab.label}
          </button>
        ))}
      </nav>

      <main className="product-detail-content">
        {activeTab === 'overview' && (
          <div className="product-detail-section">
            <h2>Product Information</h2>
            <div className="product-detail-grid">
              <div className="product-detail-field">
                <label>Product Name</label>
                <span>{product.productName || '—'}</span>
              </div>
              <div className="product-detail-field">
                <label>Category</label>
                <span>{product.productCategory || '—'}</span>
              </div>
              <div className="product-detail-field">
                <label>Price</label>
                <span>{formatCurrency(product.sellPrice)} TZS</span>
              </div>
              <div className="product-detail-field">
                <label>Stock</label>
                <span>{product.stockCount ?? '—'}</span>
              </div>
              <div className="product-detail-field">
                <label>Unit</label>
                <span>{product.unit || '—'}</span>
              </div>
              <div className="product-detail-field">
                <label>Store</label>
                <span>
                  {store ? (
                    <button
                      type="button"
                      className="product-detail-link"
                      onClick={() => navigate(`/dashboard/stores/${product.storeId}`)}
                    >
                      {store.storeName || product.storeId}
                    </button>
                  ) : (
                    product.storeId || '—'
                  )}
                </span>
              </div>
            </div>

            {specRows.length > 0 && (
              <>
                <h2 className="product-detail-specs-title">Specifications</h2>
                <div className="product-detail-grid">
                  {specRows.map((row) => (
                    <div className="product-detail-field" key={row.key}>
                      <label>{row.label}</label>
                      <span>{row.value}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'orders' && (
          <div className="product-detail-section">
            <h2>Orders ({orders.length})</h2>
            <p className="product-detail-section-desc">Orders containing this product</p>
            <div className="product-detail-table-wrap">
              <table className="product-detail-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Date</th>
                    <th>Qty</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="product-detail-empty">No orders yet</td>
                    </tr>
                  ) : (
                    orders.map((o) => (
                      <tr
                        key={o.$id}
                        className="product-detail-row-clickable"
                        onClick={() => setDetailModal({ type: 'order', item: o })}
                      >
                        <td>{o.orderId || o.$id}</td>
                        <td>{formatDate(o.orderDate)}</td>
                        <td>{o.quantity ?? '—'}</td>
                        <td>{formatCurrency(o.totalAmount)}</td>
                        <td>
                          <span className={`product-detail-status product-detail-status-${(o.status || '').toLowerCase()}`}>
                            {o.status || '—'}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="product-detail-btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDetailModal({ type: 'order', item: o });
                            }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (() => {
          const filteredOrders = filterByTimeRange(orders, analyticsTimeRange);
          const filteredUnits = filteredOrders.reduce((sum, o) => sum + (parseInt(o.quantity, 10) || 0), 0);
          const filteredRevenue = filteredOrders.reduce((sum, o) => sum + (parseFloat(o.totalAmount) || 0), 0);
          return (
            <div className="product-detail-section">
              <div className="product-detail-section-header">
                <h2>Product Analytics</h2>
                <TimeRangeSelector value={analyticsTimeRange} onChange={setAnalyticsTimeRange} />
              </div>
              <div className="product-detail-analytics">
                <div className="product-detail-analytics-card">
                  <span className="product-detail-analytics-label">Total Units Sold</span>
                  <span className="product-detail-analytics-value">{filteredUnits}</span>
                </div>
                <div className="product-detail-analytics-card">
                  <span className="product-detail-analytics-label">Total Revenue</span>
                  <span className="product-detail-analytics-value">{formatCurrency(filteredRevenue)} TZS</span>
                </div>
                <div className="product-detail-analytics-card">
                  <span className="product-detail-analytics-label">Order Count</span>
                  <span className="product-detail-analytics-value">{filteredOrders.length}</span>
                </div>
                <div className="product-detail-analytics-card">
                  <span className="product-detail-analytics-label">Avg Order Size</span>
                  <span className="product-detail-analytics-value">
                    {filteredOrders.length > 0
                      ? (filteredUnits / filteredOrders.length).toFixed(1)
                      : '—'}
                  </span>
                </div>
              </div>
            </div>
          );
        })()}

        {activeTab === 'reviews' && (
          <div className="product-detail-section">
            <div className="product-detail-section-header">
              <h2>Reviews ({reviewsSummary.count})</h2>
              {reviewsSummary.count > 0 && (
                <span className="product-detail-review-average">
                  ★ {reviewsSummary.average.toFixed(1)} average
                </span>
              )}
            </div>

            {reviewsError && <div className="product-detail-error">{reviewsError}</div>}

            {reviewsLoading && (
              <div className="product-detail-loading">
                <div className="dashboard-spinner" />
                <p>Loading reviews...</p>
              </div>
            )}

            {!reviewsLoading && reviews.length === 0 && !reviewsError && (
              <p className="product-detail-empty">No reviews yet.</p>
            )}

            {!reviewsLoading && reviews.length > 0 && (
              <div className="product-detail-reviews-list">
                {reviews.map((r) => {
                  const hidden = !!locallyHidden[r.id];
                  const busy = !!reviewActing[r.id];
                  return (
                    <div key={r.id} className={`product-detail-review-card${hidden ? ' product-detail-review-hidden' : ''}`}>
                      <div className="product-detail-review-head">
                        <span className="product-detail-review-stars">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
                        <span className="product-detail-review-author">{r.authorName || 'Anonymous'}</span>
                        <span className="product-detail-review-date">{formatDate(r.createdAt)}</span>
                        {hidden && <span className="product-detail-review-badge">Hidden</span>}
                      </div>
                      {r.comment && <p className="product-detail-review-comment">{r.comment}</p>}
                      {canModerateReviews && (
                        <div className="product-detail-review-actions">
                          {hidden ? (
                            <button
                              type="button"
                              className="product-detail-link"
                              onClick={() => toggleReviewVisibility(r, false)}
                              disabled={busy}
                            >
                              {busy ? '…' : 'Show again'}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="product-detail-link product-detail-link-danger"
                              onClick={() => toggleReviewVisibility(r, true)}
                              disabled={busy}
                            >
                              {busy ? '…' : 'Hide'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </main>

      {detailModal && (
        <StoreDetailModal
          type={detailModal.type}
          item={detailModal.item}
          data={{ orders }}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          onClose={() => setDetailModal(null)}
        />
      )}
    </div>
  );
};

export default ProductDetailPage;
